<template>
  <van-popup :value="visible" position="bottom" :style="{ height: '100vh', background: 'transparent' }" :overlay="false"
    :close-on-click-overlay="false" get-container="body" @input="$emit('update:visible', $event)">
    <div class="avatar-cropper-wrap">
      <div class="cropper-ornament cropper-ornament-left"></div>
      <div class="cropper-ornament cropper-ornament-right"></div>

      <div class="cropper-header">
        <button class="btn-cancel" @click="handleCancel">取消</button>
        <div class="header-title-wrap">
          <span class="header-title">移动和缩放</span>
          <span class="header-subtitle">调整头像显示区域</span>
        </div>
        <button class="btn-confirm" @click="handleConfirm">使用</button>
      </div>

      <div class="cropper-body">
        <div class="cropper-card">
          <vue-cropper v-show="visible" ref="cropper" :img="option.img" :output-size="option.outputSize"
            :output-type="option.outputType" :info="false" :can-scale="true" :auto-crop="true"
            :auto-crop-width="option.autoCropWidth" :auto-crop-height="option.autoCropHeight" :fixed-box="false"
            :center-box="true" :can-move="true" :can-move-box="true" :mode="'contain'" :fixed="fixed"
            :fixedNumber="fixedNumber" />
        </div>
      </div>

      <div class="cropper-footer">
        <div class="cropper-hint">拖动图片调整位置，双指缩放</div>
      </div>
    </div>
  </van-popup>
</template>

<script>
import VueCropper from 'vue-cropper/src/vue-cropper.vue'

export default {
  name: 'AvatarCropper',
  components: { VueCropper },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    imgSrc: {
      type: String,
      default: ''
    },
    fixed: {
      type: Boolean,
      default: true
    },
    fixedNumber: {
      type: Array,
      default: [1, 1]
    }
  },
  data() {
    return {
      option: {
        img: '',
        outputSize: 0.9,
        outputType: 'jpeg',
        autoCropWidth: 280,
        autoCropHeight: 280
      }
    }
  },
  watch: {
    imgSrc(val) {
      this.option.img = val
    },
    visible(val) {
      if (val) {
        // 等 van-popup 过渡动画结束后刷新，否则容器尺寸为 0 导致图片不渲染
        setTimeout(() => {
          this.option.img = this.imgSrc
          this._bindWheelScale()
          this._fitSquareCrop()
        }, 300)
      }
    }
  },
  beforeDestroy() {
    this._unbindWheelScale()
  },
  methods: {
    // vue-cropper 的滚轮缩放依赖 mouseover 触发，在移动端模拟器下不生效
    // 直接在容器上常驻绑定，绕过该限制
    _bindWheelScale() {
      const cropper = this.$refs.cropper
      if (!cropper || this._wheelBound) return
      const el = cropper.$el
      const support = 'onwheel' in document.createElement('div')
        ? 'wheel'
        : document.onmousewheel !== undefined ? 'mousewheel' : 'DOMMouseScroll'
      this._wheelHandler = e => cropper.changeSize(e)
      this._wheelSupport = support
      el.addEventListener(support, this._wheelHandler, { passive: false })
      this._wheelBound = true
    },
    _unbindWheelScale() {
      const cropper = this.$refs.cropper
      if (!cropper || !this._wheelBound) return
      cropper.$el.removeEventListener(this._wheelSupport, this._wheelHandler)
      this._wheelBound = false
    },
    // 将裁剪框设置为图片的最大内接正方形（以图片较短边为准）
    _fitSquareCrop() {
      const cropper = this.$refs.cropper
      if (!cropper) return
      // vue-cropper 加载图片后会设置 trueWidth/trueHeight
      const imgW = cropper.trueWidth || 0
      const imgH = cropper.trueHeight || 0
      if (imgW <= 0 || imgH <= 0) {
        // 图片尺寸尚未就绪，延迟重试
        setTimeout(() => this._fitSquareCrop(), 150)
        return
      }
      const size = Math.min(imgW, imgH)
      this.option.autoCropWidth = size
      this.option.autoCropHeight = size
      // 触发生成新的裁剪框
      cropper.autoCropWidth = size
      cropper.autoCropHeight = size
      cropper.goAutoCrop()
    },
    handleConfirm() {
      this.$refs.cropper.getCropBlob(blob => {
        this.$emit('confirm', blob)
        this.$emit('update:visible', false)
      })
    },
    handleCancel() {
      this.$emit('cancel')
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style lang="less" scoped>
@import url('../style/theme-variables.less');

.avatar-cropper-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #fff4f6 54%, #ffe7ec 100%);
}

.cropper-ornament {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(4px);
}

.cropper-ornament-left {
  top: -1.8rem;
  left: -1.4rem;
  width: 5.6rem;
  height: 5.6rem;
  background: rgba(255, 138, 154, 0.2);
}

.cropper-ornament-right {
  right: -1.1rem;
  bottom: 4.4rem;
  width: 4.8rem;
  height: 4.8rem;
  background: rgba(254, 181, 177, 0.16);
}

.cropper-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.9rem 1rem 0.75rem;
  flex-shrink: 0;
}

.header-title-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.btn-cancel,
.btn-confirm {
  min-width: 2.9rem;
  height: 1.7rem;
  padding: 0 0.6rem;
  border: none;
  border-radius: 0.85rem;
  font-size: 0.72rem;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.btn-cancel {
  color: @text-secondary;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 4px 12px rgba(26, 26, 26, 0.06);
}

.btn-confirm {
  color: @text-light;
  font-weight: @font-weight-semibold;
  background: linear-gradient(135deg, @primary-gradient-start 0%, @primary-gradient-end 100%);
  box-shadow: @shadow-btn;
}

.header-title {
  color: @text-primary;
  font-size: 0.82rem;
  font-weight: @font-weight-semibold;
  line-height: 1.15rem;
}

.header-subtitle {
  margin-top: 0.15rem;
  color: @text-tertiary;
  font-size: 0.58rem;
  line-height: 0.82rem;
}

.cropper-body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  padding: 0.35rem 0.8rem 0.8rem;
}

.cropper-card {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 1.25rem;
  background:
    radial-gradient(circle at 18% 18%, rgba(255, 255, 255, 0.3) 0, rgba(255, 255, 255, 0) 26%),
    radial-gradient(circle at 82% 74%, rgba(255, 224, 230, 0.4) 0, rgba(255, 224, 230, 0) 22%),
    @gradient-pink-v;
  box-shadow: 0 14px 34px rgba(255, 138, 154, 0.22);
}

.cropper-footer {
  position: relative;
  z-index: 1;
  padding: 0 0.8rem 1rem;
  flex-shrink: 0;
}

.cropper-hint {
  padding: 0.68rem 0.9rem;
  text-align: center;
  color: @text-secondary;
  font-size: 0.65rem;
  line-height: 0.92rem;
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 8px 24px rgba(26, 26, 26, 0.05);
  backdrop-filter: blur(8px);
}
</style>

<style lang="less">
@import url('../style/theme-variables.less');

/* 方形裁剪框 — 覆盖 vue-cropper 全局样式 */
.avatar-cropper-wrap .vue-cropper {
  background: transparent;

  .cropper-view-box {
    outline-color: rgba(255, 255, 255, 0.96);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.75);
  }

  .cropper-face {
    background: rgba(255, 255, 255, 0.08);
  }

  .cropper-line,
  .cropper-point {
    background-color: @primary-color;
  }

  .cropper-dashed {
    border-color: rgba(255, 255, 255, 0.45);
  }
}

@media screen and (max-width: 500px) {
  .crop-point {
    width: 30px;
    height: 30px;
  }

  .point1 {
    top: -15px;
    left: -15px;
  }

  .point3 {
    top: -15px;
    right: -15px;
  }

  .point6 {
    bottom: -15px;
    left: -15px;
  }

  .point8 {
    bottom: -15px;
    right: -15px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AvatarCropper.vue